import { useModal } from "@ebay/nice-modal-react"
import { ChevronDown, GitFork, LoaderCircle, X } from "lucide-react"
import { observer } from "mobx-react"
import { useState } from "react"
import { isHarnessId, providerModelRegistry } from "../modelRegistry"
import type { TaskDelegateTarget } from "../persistence/records"
import type { NewTaskAgentTarget } from "../persistence/newTaskAgentPins"
import type { HarnessId } from "../providerEvents/harnessEventTypes"
import type { WildLarkTaskHarness } from "../wildlark/client"
import { SessionActionPickerModal } from "./actions/SessionActionPickerModal"

export const DelegateModeControl = observer(function DelegateModeControl({
    value,
    onChange,
    harnessId,
    modelId,
    thinking,
    pinnedTargets,
    loadHarnesses,
}: {
    value: TaskDelegateTarget | null
    onChange: (value: TaskDelegateTarget | null) => Promise<void>
    harnessId: HarnessId
    modelId: string
    thinking: import("../agentTarget").ThinkingLevel
    pinnedTargets?: readonly NewTaskAgentTarget[]
    loadHarnesses: () => Promise<WildLarkTaskHarness[]>
}) {
    const picker = useModal(SessionActionPickerModal)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const selectedModel =
        value && isHarnessId(value.harnessId) ? providerModelRegistry.modelsForHarness(value.harnessId).find((model) => model.id === value.modelId) : undefined
    const label = value ? `${selectedModel?.label ?? value.modelId} · ${value.thinking ?? "Default"}` : "Off"
    const save = async (target: TaskDelegateTarget | null) => {
        setSaving(true)
        setError(null)
        try {
            await onChange(target)
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Could not save delegate")
        } finally {
            setSaving(false)
        }
    }
    const choose = () => {
        void picker.show({
            actionName: "Delegate mode",
            actionKind: "delegate",
            submitLabel: "Set delegate",
            pendingLabel: "Saving…",
            description: "Your main agent designs and reviews the work. This agent implements its specifications. Applies to your next request.",
            currentTarget: { harnessId, modelId, thinking },
            initialTarget: value ?? undefined,
            pinnedTargets,
            loadHarnesses,
            onSelect: ([target]) => onChange(target),
        })
    }
    return (
        <div className="flex shrink-0 items-center gap-0.5">
            <button
                type="button"
                className="btn flex h-7 items-center gap-1.5 px-2 text-xs text-muted hover:bg-base-200 hover:text-base-content disabled:opacity-50"
                onClick={choose}
                disabled={saving}
                title="Main agent designs and reviews; delegate implements"
                aria-label={`Delegate: ${label}`}
            >
                {saving ? <LoaderCircle size={12} className="animate-spin" /> : <GitFork size={12} />}
                <span className="whitespace-nowrap">Delegate: {label}</span>
                <ChevronDown size={10} />
            </button>
            {value && (
                <button
                    type="button"
                    className="btn flex h-7 w-6 items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                    disabled={saving}
                    onClick={() => void save(null)}
                    aria-label="Turn off delegation"
                    title="Turn off delegation"
                >
                    <X size={12} />
                </button>
            )}
            {error && (
                <span role="alert" className="max-w-80 text-xs text-error">
                    {error}
                </span>
            )}
        </div>
    )
})
